import React, { useCallback, useEffect, useState } from 'react';
import { Briefcase, Loader2, RefreshCw, Check, X } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../api';

/**
 * BD-01 — admin staged-rollout screen for the BD Assistant.
 *
 * Linking is not switching on: an admin turns each eligible person on here when
 * they are ready, the same staged model as the Slack People screen. Per person
 * it shows Zoho mapping, Outlook token health, and the last successful run.
 */

type OutlookHealth = 'healthy' | 'reconnect_required' | 'not_connected';
interface Capabilities { zoho_mapped: boolean; outlook_connected: boolean }
interface LastRun { at: string | null; status: string | null; error: string | null; last_successful_at: string | null }
interface EligibleUser {
  user: { id: number; name: string; email: string };
  enabled: boolean;
  enabled_at: string | null;
  capabilities: Capabilities;
  token_health?: { outlook: OutlookHealth };
  last_run?: LastRun;
  zoho_user: { email: string | null; name: string | null } | null;
}
interface IneligibleUser { user: { id: number; name: string; email: string }; reason: string }
interface Overview {
  eligible: EligibleUser[];
  ineligible: IneligibleUser[];
  summary: { eligible: number; enabled: number; ineligible: number };
}

const REASON_LABEL: Record<string, string> = {
  email_domain_not_permitted: 'Not a Stemmler email address',
  inactive_account: 'Account is inactive',
  no_email: 'No email on file',
};

const Toggle: React.FC<{ checked: boolean; onChange: (next: boolean) => void; disabled?: boolean; label: string }> = ({
  checked, onChange, disabled, label,
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    data-testid={`bd-rollout-toggle-${label}`}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
      checked ? 'bg-blue-600' : 'bg-gray-200'
    } ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform mt-0.5 ${checked ? 'translate-x-5' : 'translate-x-0.5'}`} />
  </button>
);

const Cap: React.FC<{ on: boolean }> = ({ on }) =>
  on ? <Check className="w-4 h-4 text-green-600" aria-label="yes" /> : <X className="w-4 h-4 text-gray-300" aria-label="no" />;

const HEALTH: Record<OutlookHealth, { label: string; cls: string }> = {
  healthy: { label: 'Healthy', cls: 'bg-green-50 text-green-700 border-green-200' },
  reconnect_required: { label: 'Reconnect needed', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  not_connected: { label: 'Not connected', cls: 'bg-gray-50 text-gray-500 border-gray-200' },
};

const HealthBadge: React.FC<{ health: OutlookHealth }> = ({ health }) => {
  const h = HEALTH[health] || HEALTH.not_connected;
  return <span className={`inline-block px-2 py-0.5 rounded-full border text-xs font-medium ${h.cls}`}>{h.label}</span>;
};

function formatWhen(iso: string | null): string {
  if (!iso) return 'Never';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? 'Never' : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

const BDAssistantRolloutCard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [busyUser, setBusyUser] = useState<number | null>(null);
  const [overview, setOverview] = useState<Overview | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const { data } = await api.get('/api/bd-assistant/admin/overview');
      setOverview(data);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setEnabled = useCallback(async (userId: number, enabled: boolean) => {
    setBusyUser(userId);
    try {
      await api.patch(`/api/bd-assistant/admin/${userId}`, { enabled });
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Could not update that person.');
    } finally {
      setBusyUser(null);
    }
  }, [load]);

  const syncZoho = useCallback(async () => {
    setSyncing(true);
    try {
      const { data } = await api.post('/api/bd-assistant/admin/sync-zoho-users', {});
      toast.success(`Zoho users synced — ${data.matched} matched, ${data.unmatched} unmatched.`);
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Could not sync Zoho users.');
    } finally {
      setSyncing(false);
    }
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-500 py-8" data-testid="bd-rollout-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading BD Assistant rollout…
      </div>
    );
  }
  if (loadError || !overview) {
    return (
      <div className="py-8 text-sm text-gray-600" data-testid="bd-rollout-error">
        Could not load the BD Assistant rollout.{' '}
        <button type="button" className="text-blue-600 underline" onClick={load}>Retry</button>
      </div>
    );
  }

  const { eligible, ineligible, summary } = overview;

  return (
    <div className="space-y-5" data-testid="bd-rollout">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <Briefcase className="w-5 h-5 text-gray-700 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-gray-900">BD Assistant — rollout</h3>
            <p className="text-sm text-gray-500">
              {summary.eligible} eligible · {summary.enabled} switched on. Switching someone on
              does not change anyone else — turn people on in batches when they are ready.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={syncZoho}
          disabled={syncing}
          data-testid="bd-rollout-sync-zoho"
          className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 bg-white text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} /> Sync Zoho users
        </button>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
              <th className="px-4 py-2 font-medium">Person</th>
              <th className="px-4 py-2 font-medium text-center">Zoho</th>
              <th className="px-4 py-2 font-medium text-center">Outlook token</th>
              <th className="px-4 py-2 font-medium">Last successful run</th>
              <th className="px-4 py-2 font-medium text-right">Switched on</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {eligible.map((e) => (
              <tr key={e.user.id} data-testid={`bd-rollout-row-${e.user.id}`}>
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-900">{e.user.name}</div>
                  <div className="text-xs text-gray-500">{e.user.email}</div>
                </td>
                <td className="px-4 py-3 text-center">
                  <div className="inline-flex justify-center"><Cap on={e.capabilities.zoho_mapped} /></div>
                </td>
                <td className="px-4 py-3 text-center" data-testid={`bd-rollout-health-${e.user.id}`}>
                  <HealthBadge health={e.token_health?.outlook || (e.capabilities.outlook_connected ? 'healthy' : 'not_connected')} />
                </td>
                <td className="px-4 py-3" data-testid={`bd-rollout-lastrun-${e.user.id}`}>
                  <div className="text-gray-700">{formatWhen(e.last_run?.last_successful_at ?? null)}</div>
                  {e.last_run?.status && e.last_run.status !== 'success' && (
                    <div className="text-xs text-amber-600" title={e.last_run.error || undefined}>
                      Last run {e.last_run.status} · {formatWhen(e.last_run.at)}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex justify-end">
                    {busyUser === e.user.id
                      ? <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                      : <Toggle label={e.user.name} checked={e.enabled} onChange={(next) => setEnabled(e.user.id, next)} />}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {ineligible.length > 0 && (
        <div data-testid="bd-rollout-ineligible">
          <div className="text-xs font-medium text-gray-500 mb-1">Cannot be switched on</div>
          <div className="rounded-lg border border-gray-100 divide-y divide-gray-50">
            {ineligible.map((u) => (
              <div key={u.user.id} className="flex items-center justify-between px-4 py-2 text-sm">
                <span className="text-gray-700">{u.user.name} <span className="text-gray-400">· {u.user.email}</span></span>
                <span className="text-xs text-gray-400">{REASON_LABEL[u.reason] || u.reason}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BDAssistantRolloutCard;
